// /client/src/pages/Admin/DashboardAdherents/VisitesLineChart.jsx
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../services/visitesServices";
import { FaTrash, FaTimes } from "react-icons/fa";

const TRANSITION_MS = 300;

export default function VisitesLineChart() {
  const [data, setData] = useState([]);
  const [panelMounted, setPanelMounted] = useState(false); // <-- montage/démontage
  const [panelOpen, setPanelOpen] = useState(false);       // <-- état animé
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchVisites = async () => {
    try {
      const response = await countVisitesByDate();
      setData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des visites:", error);
    }
  };

  useEffect(() => {
    fetchVisites();
  }, []);

  const fmtTick = (s) => {
    const d = new Date(s);
    return isNaN(d) ? s : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  };

  const fmtTooltipLabel = (s) => {
    const d = new Date(s);
    return isNaN(d)
      ? s
      : d.toLocaleDateString("fr-FR", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
  };

  const openSlide = () => {
    setPanelMounted(true);        // on monte…
    requestAnimationFrame(() => { // …puis on déclenche l’anim
      setPanelOpen(true);
    });
  };

  const closeSlide = () => {
    setPanelOpen(false);                       // lance la sortie
    setTimeout(() => setPanelMounted(false), TRANSITION_MS); // démonte après anim
  };

  const handleDelete = async () => {
    setErr("");
    if (!dateDebut || !dateFin) {
      setErr("Veuillez sélectionner les dates de début et de fin.");
      return;
    }
    if (new Date(dateDebut) > new Date(dateFin)) {
      setErr("La date de début doit être antérieure à la date de fin.");
      return;
    }

    setLoading(true);
    try {
      const response = await deleteVisitesByDate(dateDebut, dateFin);
      alert(response?.message || "Visites supprimées avec succès.");
      await fetchVisites();
      setDateDebut("");
      setDateFin("");
      closeSlide();
    } catch (error) {
      console.error("Erreur suppression visites:", error);
      setErr("Une erreur est survenue lors de la suppression.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
            Évolution des visites
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Nombre de visites par jour
          </p>
        </div>

        <button
          type="button"
          onClick={openSlide}
          title="Supprimer des visites entre deux dates"
          className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-white/90 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 ring-1 ring-slate-200/70 dark:ring-slate-700/70 hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition"
        >
          <FaTrash />
          Nettoyer
        </button>
      </div>

      {/* Chart */}
      <div style={{ position: "relative", zIndex: 0 }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 8, right: 10, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.25)" />
            <XAxis dataKey="date_visite" tickFormatter={fmtTick} stroke="rgba(30,41,59,0.7)" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} stroke="rgba(30,41,59,0.7)" tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(255,255,255,0.95)",
                border: "1px solid rgba(203,213,225,0.6)",
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(2,6,23,0.08)",
              }}
              labelFormatter={fmtTooltipLabel}
              formatter={(value) => [value, "Visites"]}
            />
            <Legend wrapperStyle={{ color: "rgba(30,41,59,0.7)" }} iconType="circle" />
            <Line type="monotone" dataKey="compteur" name="Visites" stroke="#6366F1" strokeWidth={3} dot={{ r: 2 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Backdrop + Slide-over panel (montés seulement quand nécessaire) */}
      {panelMounted && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] transition-opacity duration-${TRANSITION_MS} ${
              panelOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            onClick={() => !loading && closeSlide()}
          />

          {/* Panel */}
          <div
            className={`fixed right-0 top-0 h-full w-full max-w-sm z-[61] transform transition-transform duration-${TRANSITION_MS} ${
              panelOpen ? "translate-x-0" : "translate-x-full"
            }`}
            style={{ willChange: "transform" }}
            aria-hidden={!panelOpen}
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 shadow-2xl">
              {/* Panel header */}
              <div className="px-4 py-3 sm:px-5 flex items-center justify-between bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 text-white">
                <div>
                  <div className="text-sm font-semibold">Nettoyage des visites</div>
                  <div className="text-xs/5 opacity-90">Supprimer entre deux dates</div>
                </div>
                <button
                  type="button"
                  onClick={() => !loading && closeSlide()}
                  className="h-8 w-8 grid place-items-center rounded-lg bg-white/20 hover:bg-white/30 transition"
                  title="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Panel body */}
              <div className="p-4 sm:p-5 space-y-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                    Date de début
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    disabled={loading}
                  />
                </div>

                {err && <div className="text-sm text-rose-600 dark:text-rose-400">{err}</div>}

                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/80 transition"
                    onClick={closeSlide}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white ring-1 ring-rose-300/50 shadow-sm transition ${
                      loading ? "bg-rose-400 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700"
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 004 12z" />
                        </svg>
                        Suppression…
                      </>
                    ) : (
                      <>
                        <FaTrash />
                        Supprimer
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="px-4 pb-4 text-xs text-slate-500 dark:text-slate-400">
                Astuce : les dates sont inclusives. Les données du graphique seront
                automatiquement rafraîchies après suppression.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
